import { useRef, useState } from "react";
import type { MessageDTO, TaskAssignmentDTO } from "@easyshop/shared";
import { TASK_PRIORITY_LABELS } from "@easyshop/shared";
import { Pill, TaskStatusBadge, cx } from "@/components/ui";
import { dateFR, stamp } from "@/lib/format";
import { uploadFiles, useAssignmentAction } from "@/lib/queries";
import { useSession } from "@/hooks/useSession";

/**
 * Message du fil : simple, tâche (statut, échéance, priorité, « ✓ Fait + photo »)
 * ou annonce réseau (« Vu par N boutiques »). Comme dans une messagerie : mes
 * messages à droite sur fond vert, ceux reçus à gauche sur fond blanc. Même
 * rendu en PC et en mobile, seule la densité change.
 */
export function MessageBubble({ m, compact = false }: { m: MessageDTO; compact?: boolean }) {
  const mine = m.mine;
  const author = `${m.authorLabel}${m.author && m.side === "BOUTIQUE" && m.author.name !== m.authorLabel ? ` (${m.author.name})` : ""}`;
  return (
    <div className={cx("flex w-full", mine ? "justify-end" : "justify-start")}>
      <div className={cx("flex min-w-0 flex-col", mine ? "items-end" : "items-start", compact ? "max-w-[80%]" : "max-w-[min(78%,560px)]")}>
        <div className={cx(compact ? "mb-[3px] px-[6px] text-[12px] text-muted" : "mb-1 px-1 text-[11px] text-[oklch(0.6_0.01_150)]", mine && "text-right")}>
          {mine ? "Vous" : author} · {stamp(m.createdAt)}
        </div>
        {m.kind === "TASK" && m.assignment ? (
          <TaskCard a={m.assignment} compact={compact} ios={compact} />
        ) : m.kind === "ANNOUNCEMENT" && m.announcement ? (
          <div
            className={cx(
              "bg-accent-soft",
              compact ? "rounded-[18px] px-[14px] py-[10px]" : cx("rounded-card-sm px-4 py-[14px]", mine ? "rounded-tr-[4px]" : "rounded-tl-[4px]")
            )}
          >
            <div className={cx("text-[11px] font-bold text-accent", compact ? "tracking-[0.3px]" : "mb-1")}>ANNONCE RÉSEAU</div>
            <div className={cx("whitespace-pre-line", compact ? "mt-[2px] text-[16px] leading-[1.35]" : "text-[13px] font-semibold")}>{m.body}</div>
            <Attachments m={m} />
            {!compact && (
              <div className="mt-2 text-[11px] text-muted-2">
                Vu par {m.announcement.seenBy} boutique{m.announcement.seenBy > 1 ? "s" : ""} sur {m.announcement.recipients}
              </div>
            )}
          </div>
        ) : (
          <div
            className={cx(
              "whitespace-pre-line break-words",
              compact
                ? cx("rounded-[18px] px-[13px] py-2 text-[16px] leading-[1.35]", mine ? "bg-accent text-white" : "bg-white")
                : cx(
                    "rounded-[16px] px-[14px] py-[10px] text-[13px] leading-[1.45] shadow-[0_1px_1px_rgba(20,30,20,0.06)]",
                    mine ? "rounded-tr-[4px] bg-[oklch(0.93_0.06_150)] text-[oklch(0.22_0.03_150)]" : "rounded-tl-[4px] border border-line bg-white"
                  )
            )}
          >
            {m.body}
            <Attachments m={m} onAccent={compact && mine} />
          </div>
        )}
      </div>
    </div>
  );
}

function Attachments({ m, onAccent = false }: { m: MessageDTO; onAccent?: boolean }) {
  if (!m.attachments.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {m.attachments.map((a) =>
        a.mimeType.startsWith("image/") ? (
          <a key={a.id} href={a.url} target="_blank" rel="noreferrer">
            <img src={a.url} alt={a.fileName} className="h-24 w-24 rounded-[10px] object-cover" />
          </a>
        ) : (
          <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className={cx("text-[12px] font-semibold", onAccent ? "text-white underline" : "text-accent")}>
            📎 {a.fileName}
          </a>
        )
      )}
    </div>
  );
}

/** Carte d'une tâche (dans le fil ou dans une liste de tâches) avec ses actions ; `ios` : rendu du shell mobile. */
export function TaskCard({ a, compact = false, ios = false }: { a: TaskAssignmentDTO; compact?: boolean; ios?: boolean }) {
  const { can, me } = useSession();
  const act = useAssignmentAction();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const open = a.status !== "DONE" && a.status !== "VALIDATED";
  const needsFile = a.task.requiresInvoice || a.task.requiresProof;
  const fileLabel = a.task.requiresInvoice ? "facture" : "photo";
  const missingFile = needsFile && a.proofs.length === 0;
  // Le créateur de la tâche peut toujours la valider, la refuser ou l'invalider.
  const canReview = can("tasks.validate") || a.task.createdById === me.user.id;
  const btn = ios
    ? "min-h-[32px] min-w-0 flex-1 truncate whitespace-nowrap rounded-[8px] bg-ios-fill px-[10px] text-[14px] font-semibold text-accent disabled:opacity-40"
    : "rounded-full border border-line bg-white px-[14px] py-[7px] text-[12px] font-bold disabled:opacity-50";
  const btnMain = ios
    ? "min-h-[32px] min-w-0 flex-1 truncate whitespace-nowrap rounded-[8px] bg-accent px-[10px] text-[14px] font-semibold text-white disabled:opacity-40"
    : "rounded-full bg-accent px-[14px] py-[7px] text-[12px] font-bold text-white disabled:opacity-50";

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action impossible");
    }
  };

  // La photo / facture se joint à la tâche, séparément du bouton « Fait ».
  const attach = (files: FileList | null) =>
    run(async () => {
      if (!files || files.length === 0) return;
      setUploading(true);
      try {
        const up = await uploadFiles([...files]);
        await act.mutateAsync({ id: a.id, action: "proof", proofAttachmentIds: up.map((p) => p.id) });
      } finally {
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
      }
    });

  return (
    <div
      className={cx(
        ios ? "rounded-ios bg-white px-[14px] py-3" : "rounded-card-sm border border-line",
        !ios && (compact ? "bg-white px-[15px] py-[13px]" : "bg-page px-4 py-[14px]")
      )}
    >
      <div className={cx("flex items-center", ios ? "gap-2" : "gap-[10px]")}>
        <span className={cx("flex-1", ios ? "text-[16px] font-semibold" : "text-[13px] font-bold")}>{a.task.title}</span>
        <TaskStatusBadge status={a.status} late={a.late} className={cx("flex-shrink-0", ios && "!rounded-[6px] !px-[7px] !py-[2px] !text-[12px] !font-semibold")} />
      </div>
      {a.task.description && <div className={cx("mt-[6px] whitespace-pre-line text-ink-2", ios ? "text-[15px]" : "text-[12.5px]")}>{a.task.description}</div>}
      <div className={cx(ios ? "mt-1 text-[13px] text-muted" : "mt-2 text-[11.5px] text-muted-2")}>
        {a.task.dueAt ? `Échéance ${dateFR(a.task.dueAt)}` : "Sans échéance"} · Priorité {TASK_PRIORITY_LABELS[a.task.priority]}
        {needsFile && open ? ` · ${a.task.requiresInvoice ? "Facture requise" : "Photo requise"}` : ""}
      </div>
      {a.refusalReason && a.status === "REFUSED" && (
        <div className="mt-2 text-[12px] font-semibold text-danger">À reprendre : {a.refusalReason}</div>
      )}
      {a.proofs.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Pill className="bg-positive-bg text-[11px] font-semibold text-[oklch(0.45_0.13_152)]">
            {a.task.requiresInvoice ? "Facture jointe" : "Photo jointe"}
          </Pill>
          {a.proofs.map((p) =>
            p.mimeType.startsWith("image/") ? (
              <a key={p.id} href={p.url} target="_blank" rel="noreferrer">
                <img src={p.url} alt={p.fileName} className="h-10 w-10 rounded-[8px] object-cover" />
              </a>
            ) : (
              <a key={p.id} href={p.url} target="_blank" rel="noreferrer" className="text-[12px] font-semibold text-accent">
                📎 {p.fileName}
              </a>
            )
          )}
        </div>
      )}
      <div className={cx("mt-[10px] flex", ios ? "gap-[6px]" : "flex-wrap gap-2")}>
        {open && can("tasks.complete") && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept={a.task.requiresInvoice ? "image/*,application/pdf" : "image/*"}
              multiple
              className="hidden"
              onChange={(e) => void attach(e.target.files)}
            />
            <button type="button" disabled={uploading || act.isPending} onClick={() => fileRef.current?.click()} className={btn}>
              {uploading
                ? "Envoi…"
                : ios
                  ? // Mobile : libellés courts pour tenir sur une ligne avec « En cours » et « Fait ».
                    `${a.proofs.length > 0 ? "+" : "📎"} ${a.task.requiresInvoice ? "Facture" : "Photo"}`
                  : a.proofs.length > 0
                    ? `+ Ajouter ${a.task.requiresInvoice ? "une facture" : "une photo"}`
                    : `📎 Joindre ${a.task.requiresInvoice ? "la facture" : "une photo"}`}
            </button>
            {a.status !== "IN_PROGRESS" && (
              <button type="button" onClick={() => void run(() => act.mutateAsync({ id: a.id, action: "start" }))} className={btn}>
                En cours
              </button>
            )}
            <button
              type="button"
              disabled={act.isPending || uploading || missingFile}
              title={missingFile ? `Joignez d'abord la ${fileLabel}` : undefined}
              onClick={() => void run(() => act.mutateAsync({ id: a.id, action: "done" }))}
              className={btnMain}
            >
              {ios ? "Fait" : "✓ Fait"}
            </button>
          </>
        )}
        {a.status === "DONE" && canReview && (
          <>
            <button type="button" onClick={() => void run(() => act.mutateAsync({ id: a.id, action: "validate" }))} className={btnMain}>
              Valider
            </button>
            <button
              type="button"
              onClick={() => {
                const reason = window.prompt("Motif (facultatif) :") ?? undefined;
                void run(() => act.mutateAsync({ id: a.id, action: "refuse", reason }));
              }}
              className={cx(btn, "text-danger")}
            >
              Refuser
            </button>
          </>
        )}
        {a.status === "VALIDATED" && canReview && (
          <button
            type="button"
            onClick={() => {
              const reason = window.prompt("Motif de l'invalidation (facultatif) :");
              if (reason === null) return;
              void run(() => act.mutateAsync({ id: a.id, action: "refuse", reason: reason || undefined }));
            }}
            className={cx(btn, "text-danger")}
          >
            Invalider
          </button>
        )}
      </div>
      {open && missingFile && can("tasks.complete") && (
        <div className="mt-2 text-[11.5px] text-muted-2">Joignez la {fileLabel} pour pouvoir marquer la tâche comme faite.</div>
      )}
      {error && <div className="mt-2 text-[12px] font-semibold text-danger">{error}</div>}
    </div>
  );
}
